<script lang="ts">
	import { TeaserType } from '../../domain/teaser/TeaserType.ts';
	import { PointsInfoBannerType } from '../../domain/points-info-banner/PointsInfoBannerType.ts';
	import type { PointsInfoBannerContent } from '../../domain/points-info-banner/PointsInfoBannerContent.ts';
	import { getURLForLogin } from '../../services/http/LoginURLHelper.ts';
	import { toNewProgramStructureSignUpSheet } from '../../domain/SheetData.ts';
	import { TeaserTouchPoint } from '../../domain/teaser/TeaserUtils.ts';
	import type { TeaserContent } from '../../domain/teaser/TeaserContent.ts';
	import TrackingService from '../../services/tracking/TrackingService.ts';
	import { onMount } from 'svelte';
	import { trackClick, trackSeen, trackView } from '../../services/tracking/TeaserTracking.ts';
	import { onViewport } from '../../services/tracking/IntersectionObserver';
	import { sheetV1 } from '@otto-ec/otto-components/sheet';

	type Props = {
		teaserType: TeaserType;
		pointsInfoBannerType: PointsInfoBannerType;
		pointsInfoBannerContent?: PointsInfoBannerContent;
		teaserContent?: TeaserContent;
	}
	let { teaserType, pointsInfoBannerType, pointsInfoBannerContent, teaserContent }: Props = $props();

	const teaserSize = 'MediaObject25';
	let teaser: Element;
	let trackingService: TrackingService;

	let redirectUrl: string | undefined;
	const isToLoginType = teaserType === TeaserType.TO_LOGIN || pointsInfoBannerType === PointsInfoBannerType.TO_LOGIN;
	const isBasicAndPlusType = teaserType === TeaserType.BASIC_AND_PLUS_TEASER || pointsInfoBannerType === PointsInfoBannerType.JOIN_BASIC_AND_PLUS;


	onMount(async () => {
		trackingService = new TrackingService();
		trackClick(trackingService, teaser, undefined, teaserType, teaserSize);
		trackView(trackingService, teaser, undefined, teaserType, teaserSize);

		if (isToLoginType) {
			redirectUrl = await getURLForLogin(window.location.href);
		}
	});

	function open() {
		if (isToLoginType && redirectUrl) {
			window.location.assign(redirectUrl);
		} else if (isBasicAndPlusType && teaserContent) {
			sheetV1.create({ open: true, ...toNewProgramStructureSignUpSheet(teaserType, teaserContent, TeaserTouchPoint.DETAILVIEW) });
		}
	}
</script>

<div data-testid="combinedInfo-teaser-container" id="up-combined-info-teaser-container"
	bind:this={teaser}
	onenterViewport={() => trackSeen(trackingService)}
	use:onViewport
>
	<oc-card-v2
		oc-aria-label="OTTO UP dein Vorteilsprogramm"
		data-testid="combinedInfo-teaser-container-card"
		style="--background-color:var(--oc-semantic-color-background-soft-pink)"
		class="combinedInfo-teaser"
		as-button="true"
		onclick={open}
	>
		<div class="combinedInfo-teaser__content oc-mt-50 oc-mx-50 oc-br-100 oc-background-color-canvas">
			<div class="combinedInfo-teaser__content__headline oc-p-100">
				<span class="oc-headline-50">Spare beim Shoppen</span>
				<oc-logo-v1 data-testid="headline-logo" class="combinedInfo-teaser__content__headline__logo" logo="otto-up"
					fill-parent></oc-logo-v1>
			</div>
			<div class="combinedInfo-teaser__content__benefits oc-px-100 oc-pb-100"
				data-testid="combinedInfo-teaser__content__benefits-list">
				<ul>
					{#if teaserType !== TeaserType.NO_CONTENT }
						<li class="benefits-list-item">
							<oc-icon-v1 size="50" aria-hidden="true" class="level-info-list-icon" type='check'></oc-icon-v1>
							<p class="oc-copy-75">GRATIS Versand ab 59,95 € Bestellwert</p>
						</li>
					{/if}
					{#if pointsInfoBannerType !== PointsInfoBannerType.NO_CONTENT }
						<li class ="benefits-list-item">
							<oc-icon-v1 size="50" aria-hidden="true" class="level-info-list-icon" type='check'></oc-icon-v1>
							<p class="oc-copy-75">{pointsInfoBannerContent?.description}</p>
						</li>
					{/if}
					<li class="benefits-list-item">
						<oc-icon-v1 size="50" aria-hidden="true" class="level-info-list-icon" type='check'></oc-icon-v1>
						<p class="oc-copy-75">Extra-Rabatte nur für Mitglieder</p>
					</li>
				</ul>
			</div>
		</div>
		<div class="combinedInfo-teaser__cta-info oc-mt-75 oc-mb-100">
			<oc-link-v2 data-testid="combinedInfo-teaser-cta" variant="over-color-bold" size="75">
				<a>Jetzt alle Vorteile ansehen</a>
			</oc-link-v2>
		</div>
	</oc-card-v2>
</div>
<style lang="scss">
  @use '@otto-ec/otto-components-utils/scss/mixins' as mixins;
  @use "@otto-ec/design-tokens/tokens";
  @use "sass:map";

  .combinedInfo-teaser {
    &__content {
      &__headline {
        display: flex;
        justify-content: space-between;
        align-items: center;

        &__logo {
          height: 16px;
          width: 63px;
          display: inline-block;
        }
      }

      &__benefits {
        .benefits-list-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;

          p {
            margin-bottom: 0;
          }
        }

        .benefits-list-item:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__cta-info {
      display: flex;
      justify-content: center;
    }
  }
</style>
